import { ReleaseState } from '@/model/ReleaseState';

export type TemplateLayout = 'TABLE' | 'MATRIX';

export const TEMPLATE_LAYOUTS: TemplateLayout[] = ['TABLE', 'MATRIX'];

export type TemplateSearchPart = Pick<Template, 'artifactId' | 'versionId' | 'version' | 'releaseState' | 'documentKey' | 'name'>

export interface Template {
    artifactId: string;
    versionId: string;
    version: number;
    releaseState: ReleaseState;
    name: string;
    description: string;
    layout: TemplateLayout;
    documentKey: string;
    reviewerRoleId: string;
    releaserRoleId: string;
    location: string;
    createdTemplateContentIds: string[];
    reportedTemplateContentIds: string[];
    templateContentOrder: string[];
}

export interface TemplateOverallStatistics {
    latestReleasedCount: number;
    countByReleaseState: { [key: string]: number };
}

export const NEW_TEMPLATE_ID = 'NEW_TEMPLATE_ID';

export function createEmptyTemplate(): Template {
    return {
        artifactId: '',
        versionId: '',
        version: 1,
        releaseState: 'DRAFT',
        name: '',
        description: '',
        layout: 'TABLE',
        documentKey: '',
        location: '',
        reviewerRoleId: '',
        releaserRoleId: '',
        createdTemplateContentIds: [],
        reportedTemplateContentIds: [],
        templateContentOrder: []
    };
}

/**
 * Common order of created and reported template contents: keeps the given order, drops ids that are no longer
 * linked and appends newly linked ids at the end (same logic as in the backend).
 */
export function normalizeTemplateContentOrder(template: Partial<Pick<Template, 'createdTemplateContentIds' | 'reportedTemplateContentIds' | 'templateContentOrder'>>): string[] {
    const linkedIds = [...(template.createdTemplateContentIds || []), ...(template.reportedTemplateContentIds || [])];
    const orderedIds = (template.templateContentOrder || []).filter(id => linkedIds.includes(id));
    return [...new Set([...orderedIds, ...linkedIds])];
}

/**
 * Sorts the given template content ids according to the common order of the template.
 */
export function sortByTemplateContentOrder(template: Template, templateContentIds: string[]): string[] {
    return normalizeTemplateContentOrder(template).filter(id => templateContentIds.includes(id));
}

export function createNewTemplate(): Template {
    return { ...createEmptyTemplate(), versionId: NEW_TEMPLATE_ID };
}